/**
 * PDF Export Utility
 * Handles PDF generation with professional formatting for all data types
 * Uses jsPDF and jspdf-autotable for table generation
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { generateFilename } from './csv-export';

export interface PDFColumn {
  header: string;
  dataKey: string;
  width?: number;
  transform?: (value: unknown, row?: Record<string, unknown>) => string;
}

export interface PDFExportOptions {
  title: string;
  subtitle?: string;
  carrierName?: string;
  dotNumber?: number;
  orientation?: 'portrait' | 'landscape';
  pageSize?: 'a4' | 'letter';
  showLogo?: boolean;
  showMetadata?: boolean;
  headerColor?: [number, number, number];
}

// Platform brand colors
const COLORS = {
  primary: [37, 99, 235] as [number, number, number],      // Blue
  danger: [220, 38, 38] as [number, number, number],       // Red
  warning: [245, 158, 11] as [number, number, number],     // Orange/Yellow
  success: [34, 197, 94] as [number, number, number],      // Green
  gray: [107, 114, 128] as [number, number, number],       // Gray
  dark: [17, 24, 39] as [number, number, number],          // Dark gray
};

/**
 * Add header to PDF document
 */
const addPDFHeader = (
  doc: jsPDF,
  options: PDFExportOptions
): number => {
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 15;

  // Title
  doc.setFontSize(18);
  doc.setTextColor(...COLORS.dark);
  doc.setFont('helvetica', 'bold');
  doc.text(options.title, 14, yPos);
  yPos += 8;

  // Subtitle
  if (options.subtitle) {
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.gray);
    doc.setFont('helvetica', 'normal');
    doc.text(options.subtitle, 14, yPos);
    yPos += 6;
  }

  // Carrier info
  if (options.carrierName || options.dotNumber) {
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.dark);
    const carrierInfo = [
      options.carrierName && `Carrier: ${options.carrierName}`,
      options.dotNumber && `DOT#: ${options.dotNumber}`,
    ].filter(Boolean).join(' | ');
    doc.text(carrierInfo, 14, yPos);
    yPos += 6;
  }

  // Metadata line
  if (options.showMetadata !== false) {
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.gray);
    doc.setFont('helvetica', 'italic');
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPos);
    yPos += 4;
  }

  // Horizontal line
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(14, yPos, pageWidth - 14, yPos);
  yPos += 8;

  return yPos;
};

/**
 * Add footer to PDF document
 */
const addPDFFooter = (doc: jsPDF): void => {
  const pageCount = doc.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.gray);
    doc.setFont('helvetica', 'normal');

    // Page number
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );

    // Footer text
    doc.text(
      'Transportation Safety Platform - Confidential',
      14,
      pageHeight - 10
    );
  }
};

/**
 * Generic PDF export function with professional formatting
 */
export const exportToPDF = async (
  data: Record<string, unknown>[],
  columns: PDFColumn[],
  options: PDFExportOptions,
  onProgress?: (progress: number) => void
): Promise<void> => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  const doc = new jsPDF({
    orientation: options.orientation || 'landscape',
    unit: 'mm',
    format: options.pageSize || 'letter',
  });

  // Add header
  const startY = addPDFHeader(doc, options);

  // Record count
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.dark);
  doc.setFont('helvetica', 'normal');
  doc.text(`Total Records: ${data.length.toLocaleString()}`, 14, startY);

  // Transform data for table
  const transformedData: unknown[][] = data.map(row => {
    return columns.map(col => {
      const value = getNestedValue(row, col.dataKey);
      return col.transform ? col.transform(value, row) : formatValue(value);
    });
  });

  // Table configuration
  const tableOptions: Record<string, unknown> = {
    startY: startY + 6,
    head: [columns.map(c => c.header)],
    body: transformedData,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: 'linebreak',
      lineColor: [200, 200, 200],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: options.headerColor || COLORS.primary,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'left',
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250],
    },
    columnStyles: columns.reduce((acc, col, idx) => {
      if (col.width) {
        acc[idx] = { cellWidth: col.width };
      }
      return acc;
    }, {} as Record<number, { cellWidth: number }>),
    didDrawPage: () => {
      if (onProgress) {
        const currentPage = doc.getNumberOfPages();
        onProgress(Math.min(90, currentPage * 10));
      }
    },
  };

  autoTable(doc, tableOptions);

  // Add footer
  addPDFFooter(doc);

  if (onProgress) onProgress(100);

  // Generate filename and save
  const filename = generateFilename(
    options.title.toLowerCase().replace(/\s+/g, '_'),
    options.carrierName,
    'pdf'
  );
  doc.save(filename);
};

// ==========================================
// SPECIALIZED EXPORT FUNCTIONS
// ==========================================

/**
 * Export violations to PDF
 */
export const exportViolationsToPDF = async (
  violations: Record<string, unknown>[],
  carrierName?: string,
  dotNumber?: number,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const columns: PDFColumn[] = [
    { header: 'Date', dataKey: 'inspectionDate', width: 22, transform: formatDate },
    { header: 'Code', dataKey: 'violationCode', width: 25 },
    { header: 'Description', dataKey: 'description', width: 60 },
    { header: 'BASIC', dataKey: 'basicCategory', width: 25 },
    { header: 'Severity', dataKey: 'severity', width: 18 },
    { header: 'OOS', dataKey: 'outOfServiceIndicator', width: 12, transform: (v) => v === 'Y' ? 'Yes' : 'No' },
    { header: 'State', dataKey: 'inspectionState', width: 14 },
    { header: 'VIN', dataKey: 'vin', width: 40 },
  ];

  await exportToPDF(violations, columns, {
    title: 'Violations Report',
    subtitle: 'Detailed violation history sorted by severity',
    carrierName,
    dotNumber,
    orientation: 'landscape',
    headerColor: COLORS.danger,
  }, onProgress);
};

/**
 * Export inspections to PDF
 */
export const exportInspectionsToPDF = async (
  inspections: Record<string, unknown>[],
  carrierName?: string,
  dotNumber?: number,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const columns: PDFColumn[] = [
    { header: 'Date', dataKey: 'insp_date', width: 22, transform: formatDate },
    { header: 'Inspection #', dataKey: 'inspection_id', width: 28 },
    { header: 'State', dataKey: 'reportState', width: 15 },
    { header: 'Level', dataKey: 'inspLevelId', width: 14 },
    { header: 'Location', dataKey: 'locationDesc', width: 40 },
    { header: 'Violations', dataKey: 'violTotal', width: 18 },
    { header: 'OOS', dataKey: 'oosTotal', width: 12 },
    { header: 'Shipper', dataKey: 'shipperName', width: 45 },
    { header: 'Primary VIN', dataKey: 'primaryVin', width: 38 },
  ];

  await exportToPDF(inspections, columns, {
    title: 'Inspections Report',
    subtitle: 'Complete inspection history with shipper information',
    carrierName,
    dotNumber,
    orientation: 'landscape',
    headerColor: COLORS.primary,
  }, onProgress);
};

/**
 * Export crashes to PDF
 */
export const exportCrashesToPDF = async (
  crashes: Record<string, unknown>[],
  carrierName?: string,
  dotNumber?: number,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const columns: PDFColumn[] = [
    { header: 'Date', dataKey: 'reportDate', width: 22, transform: formatDate },
    { header: 'Crash ID', dataKey: 'crashId', width: 25 },
    { header: 'State', dataKey: 'reportState', width: 14 },
    { header: 'City', dataKey: 'city', width: 28 },
    { header: 'Location', dataKey: 'location', width: 35 },
    { header: 'VIN', dataKey: 'vehicleIdentificationNumber', width: 38 },
    { header: 'Fatal', dataKey: 'fatalities', width: 14 },
    { header: 'Injuries', dataKey: 'injuries', width: 16 },
    { header: 'Tow', dataKey: 'towAway', width: 12, transform: (v) => v === 'Y' ? 'Yes' : 'No' },
    { header: 'Hazmat', dataKey: 'hazmatReleased', width: 14, transform: (v) => v === 'Y' ? 'Yes' : 'No' },
  ];

  await exportToPDF(crashes, columns, {
    title: 'Crash Report',
    subtitle: 'Complete crash history with severity details',
    carrierName,
    dotNumber,
    orientation: 'landscape',
    headerColor: COLORS.danger,
  }, onProgress);
};

/**
 * Export BASIC scores to PDF
 */
export const exportBASICScoresToPDF = async (
  basics: Record<string, unknown>[],
  carrierName?: string,
  dotNumber?: number,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const columns: PDFColumn[] = [
    { header: 'BASIC Category', dataKey: 'basic.basicsType.basicsShortDesc', width: 35 },
    { header: 'Percentile', dataKey: 'basic.basicsPercentile', width: 20 },
    { header: 'Threshold', dataKey: 'basic.basicsViolationThreshold', width: 20 },
    { header: 'Exceeded', dataKey: 'basic.exceededFMCSAInterventionThreshold', width: 20, transform: (v) => v === 'Y' ? 'YES' : 'No' },
    { header: 'Insp w/ Viol', dataKey: 'basic.totalInspectionWithViolation', width: 25 },
    { header: 'Total Viol', dataKey: 'basic.totalViolation', width: 20 },
    { header: 'Risk Level', dataKey: 'basic.riskLevel', width: 20 },
    { header: 'Last Updated', dataKey: 'basic.basicsRunDate', width: 28, transform: formatDate },
  ];

  await exportToPDF(basics, columns, {
    title: 'BASIC Scores Report',
    subtitle: 'Safety Measurement System (SMS) BASIC category scores',
    carrierName,
    dotNumber,
    orientation: 'landscape',
    headerColor: COLORS.warning,
  }, onProgress);
};

/**
 * Export fleet vehicles to PDF
 */
export const exportFleetToPDF = async (
  vehicles: Record<string, unknown>[],
  carrierName?: string,
  dotNumber?: number,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const columns: PDFColumn[] = [
    { header: 'VIN', dataKey: 'vin', width: 40 },
    { header: 'Status', dataKey: 'vinStatus', width: 18 },
    { header: 'Make', dataKey: 'make', width: 22 },
    { header: 'Model', dataKey: 'model', width: 22 },
    { header: 'Year', dataKey: 'year', width: 14 },
    { header: 'Type', dataKey: 'unitType', width: 20, transform: formatUnitType },
    { header: 'Insp', dataKey: 'inspectionCount', width: 14 },
    { header: 'Viol', dataKey: 'violationCount', width: 14 },
    { header: 'Crash', dataKey: 'crashCount', width: 14 },
    { header: 'Multi-Carrier', dataKey: 'multiCarrier', width: 22, transform: (v) => v ? 'Yes' : 'No' },
  ];

  await exportToPDF(vehicles, columns, {
    title: 'Fleet Report',
    subtitle: 'Complete fleet inventory with safety metrics',
    carrierName,
    dotNumber,
    orientation: 'landscape',
    headerColor: COLORS.success,
  }, onProgress);
};

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Get nested object value using dot notation
 */
const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
  return path.split('.').reduce((current: unknown, key: string) => {
    return (current as Record<string, unknown>)?.[key];
  }, obj);
};

/**
 * Format value for PDF output
 */
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
};

/**
 * Format date for export
 */
const formatDate = (value: unknown): string => {
  if (!value) return '-';
  try {
    const date = new Date(value as string | number | Date);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch {
    return String(value);
  }
};

/**
 * Format unit type for export
 */
const formatUnitType = (value: unknown): string => {
  const unitTypeMap: Record<number, string> = {
    9: 'Trailer',
    11: 'Truck/Tractor',
  };
  return unitTypeMap[value as number] || String(value || '-');
};

/**
 * Create summary PDF with multiple sections
 */
export const exportSummaryPDF = async (
  data: {
    profile?: Record<string, unknown>;
    violations?: Record<string, unknown>[];
    inspections?: Record<string, unknown>[];
    crashes?: Record<string, unknown>[];
    basics?: Record<string, unknown>[];
  },
  carrierName?: string,
  dotNumber?: number
): Promise<void> => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter',
  });

  let yPos = addPDFHeader(doc, {
    title: 'Carrier Safety Summary',
    subtitle: 'Executive overview of safety metrics',
    carrierName,
    dotNumber,
  });

  // Profile summary section
  if (data.profile) {
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('Carrier Profile', 14, yPos);
    yPos += 8;

    const profileItems = [
      ['Legal Name', data.profile.legalName],
      ['DOT Number', data.profile.dotNumber?.toString()],
      ['Status', data.profile.allowedToOperate === 'Y' ? 'Authorized' : 'Not Authorized'],
      ['Safety Rating', data.profile.safetyRating || 'Unrated'],
      ['Total Drivers', data.profile.totalDrivers?.toString()],
      ['Total Power Units', data.profile.totalPowerUnits?.toString()],
    ];

    doc.setFontSize(10);
    doc.setTextColor(...COLORS.dark);
    doc.setFont('helvetica', 'normal');

    profileItems.forEach(([label, value]) => {
      doc.text(`${label}: ${value || 'N/A'}`, 14, yPos);
      yPos += 5;
    });
    yPos += 5;
  }

  // Statistics summary
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Safety Statistics', 14, yPos);
  yPos += 8;

  const stats = [
    ['Total Violations', data.violations?.length?.toString() || '0'],
    ['Total Inspections', data.inspections?.length?.toString() || '0'],
    ['Total Crashes', data.crashes?.length?.toString() || '0'],
    ['BASIC Categories Tracked', data.basics?.length?.toString() || '0'],
  ];

  doc.setFontSize(10);
  doc.setTextColor(...COLORS.dark);
  doc.setFont('helvetica', 'normal');

  stats.forEach(([label, value]) => {
    doc.text(`${label}: ${value}`, 14, yPos);
    yPos += 5;
  });

  // Add footer
  addPDFFooter(doc);

  const filename = generateFilename('carrier_summary', carrierName, 'pdf');
  doc.save(filename);
};
